import type { LocaleContent } from "@/types/site";

export const esContent: LocaleContent = {
  site: {
    description:
      "Diseño, decoración, planificación, coordinación de suplidores y manejo del día del evento para celebraciones cuidadas en Puerto Rico.",
    name: "JPL Atelier",
    tagline:
      "Planificación de lujo sereno para bodas, eventos corporativos y celebraciones selectas.",
  },
  navigation: [
    { href: "/about", label: "Nosotros" },
    { href: "/services", label: "Servicios" },
    { href: "/process", label: "Proceso" },
  ],
  footerGroups: [
    {
      title: "Explorar",
      links: [
        { href: "/about", label: "Nosotros" },
        { href: "/services", label: "Servicios" },
        { href: "/process", label: "Proceso" },
      ],
    },
    {
      title: "Planificar",
      links: [
        { href: "/inquiry", label: "Comenzar consulta" },
        { href: "/legal", label: "Privacidad y términos" },
      ],
    },
  ],
  pages: {
    home: {
      eyebrow: "Atelier de eventos de lujo",
      title: "Planificación y diseño de eventos con una dirección clara.",
      description:
        "JPL Atelier es el hogar digital de Joleany Event Planner: diseño, decoración, planificación, coordinación de suplidores y manejo del día del evento para bodas, eventos corporativos, eventos de lujo y celebraciones senior 18+.",
    },
    about: {
      eyebrow: "Sobre el atelier",
      title:
        "Un estudio de planificación guiado por gusto, orden y atención cercana.",
      description:
        "Conoce la forma en que Joleany trabaja el diseño, la planificación y la ejecución presencial.",
    },
    services: {
      eyebrow: "Servicios",
      title:
        "Diseño, decoración, planificación y coordinación para eventos pulidos.",
      description:
        "Encuentra el servicio que encaja con la etapa en la que estás: primeras ideas, decoración, suplidores o manejo del día del evento.",
    },
    process: {
      eyebrow: "Proceso",
      title: "Un proceso que mantiene claras las decisiones.",
      description:
        "El proceso pasa por formulario, moodboard, espacio, decoración digital, suplidores y manejo del día del evento.",
    },
    lookbook: {
      eyebrow: "Lookbook",
      title: "La dirección visual detrás del atelier.",
      description:
        "Mira referencias de mood, styling y detalles que muestran el gusto de lujo sereno de la marca.",
    },
    inquiry: {
      eyebrow: "Consulta",
      title: "Cuéntanos qué estás planificando.",
      description:
        "Comparte el tipo de evento, lugar, prioridades y dónde necesitas ayuda. El estudio recomendará el servicio adecuado.",
    },
    legal: {
      eyebrow: "Privacidad y términos",
      title: "Privacidad y términos",
      description:
        "Una explicación simple sobre la información de consulta, el contenido del sitio y las confirmaciones posteriores.",
    },
  },
  home: {
    hero: {
      eyebrow: "JPL Atelier by Joleany",
      title: "Eventos Curados\nDiseñados Con Intención",
      description:
        "Diseño, decoración y planificación para bodas, eventos corporativos, celebraciones senior y eventos privados selectos.",
      ctaHref: "/inquiry",
      ctaLabel: "Comenzar consulta",
      secondaryHref: "/services",
      secondaryLabel: "Explorar servicios",
    },
    brandIntro: {
      eyebrow: "El enfoque del atelier",
      title:
        "Diseño claro. Planificación cuidadosa. Un día de evento más tranquilo.",
      body: [
        "JPL Atelier es el hogar digital de Joleany Event Planner, un estudio de eventos en Puerto Rico enfocado en diseño, decoración física y digital, planificación completa, coordinación de suplidores y manejo del día del evento.",
        "El trabajo es limpio, cálido y sobrio. Cada proyecto empieza con el look y el espacio, luego pasa a suplidores, tiempos, montaje y el día del evento.",
      ],
      highlights: [
        "Dirección liderada por la fundadora desde las primeras ideas hasta el montaje final.",
        "Moodboard, espacio, decoración y suplidores pensados como un mismo sistema.",
        "Coordinación serena para bodas, eventos corporativos, eventos de lujo y celebraciones senior 18+.",
      ],
    },
    servicesPreview: {
      eyebrow: "Servicios",
      title: "Elige la ayuda que tu evento realmente necesita.",
      description:
        "Los servicios responden a necesidades concretas: definir el look, decorar el espacio, manejar suplidores o supervisar el día del evento.",
      items: [
        {
          title: "Diseño de Eventos",
          description:
            "Moodboards, paletas, layouts y notas de styling para definir el look antes de producir.",
          image: "/Forest-Photos/Forest-1.png",
        },
        {
          title: "Decoración",
          description:
            "Planificación de decoración física y digital para ceremonia, recepción, puntos focales y detalles visibles para invitados.",
          image: "/Forest-Photos/Forest-2.png",
        },
        {
          title: "Planificación Completa",
          description:
            "Planificación de logística, suplidores, diseño, tiempos y detalles del día del evento.",
          image: "/Forest-Photos/Forest-3.png",
        },
      ],
    },
    processPreview: {
      eyebrow: "Proceso",
      title: "Un camino estable hacia el día del evento.",
      description:
        "El proceso le da un lugar a cada decisión: formulario, look, espacio, suplidores, montaje y supervisión final.",
      image: "/Forest-Photos/Forest-4.png",
      steps: [
        {
          title: "Definir la visión",
          description:
            "El formulario recoge el tipo de evento, las prioridades, la experiencia de invitados y el nivel de ayuda necesario.",
        },
        {
          title: "Diseñar la experiencia",
          description:
            "Moodboards, planificación del espacio y previsualizaciones de decoración digital definen el look antes de producir.",
        },
        {
          title: "Coordinar la ejecución",
          description:
            "La comunicación con suplidores, confirmaciones finales, supervisión de montaje y manejo del día del evento mantienen todo organizado.",
        },
      ],
    },
    eventTypes: {
      eyebrow: "Tipos de eventos",
      title:
        "Para bodas, eventos corporativos, eventos de lujo y celebraciones senior.",
      description:
        "El estudio trabaja eventos donde el diseño, el flujo de invitados y la coordinación profesional deben cuidarse bien.",
      items: [
        {
          title: "Bodas",
          description:
            "Bodas con una dirección de diseño clara, planificación cuidadosa y manejo estable el día del evento.",
        },
        {
          title: "Eventos corporativos",
          description:
            "Encuentros empresariales y momentos de marca con una atmósfera pulida y un flujo profesional.",
        },
        {
          title: "Eventos de lujo",
          description:
            "Celebraciones privadas para clientes que quieren un look sobrio, buenos detalles y una dirección que se sienta propia.",
        },
        {
          title: "Celebraciones senior 18+",
          description:
            "Celebraciones de logro con un look maduro, buen styling y una sensación más editorial.",
        },
      ],
    },
    cta: {
      eyebrow: "Comienza con los detalles",
      title: "¿Planificas una celebración que necesita dirección de diseño?",
      description:
        "Envía el tipo de evento, lugar, prioridades y etapa de planificación. El estudio te ayudará a escoger el próximo paso.",
      href: "/inquiry",
      label: "COMENZAR UNA CONSULTA",
    },
  },
  sections: {
    aboutFounder: {
      eyebrow: "Planificación liderada por la fundadora",
      title: "Una mirada serena detrás del trabajo.",
      body: [
        "Joleany lidera cada evento con una dirección de diseño clara, un ritmo de planificación ordenado y atención a los detalles que los invitados sí notan.",
      ],
      highlights: [
        "Dirección creativa guiada por lujo, sobriedad y calidez.",
        "Planificación que conecta el look, los suplidores, los tiempos y el montaje.",
      ],
    },
    aboutPhilosophy: {
      eyebrow: "Filosofía de diseño",
      title: "El lujo sereno también está en saber qué dejar fuera.",
      description:
        "Un buen evento no necesita exceso. Joleany construye el moodboard, el layout, la decoración y la logística alrededor de la sensación que debe tener la celebración, y luego edita hasta que todo se vea limpio y completo.",
    },
    aboutDifferentiators: {
      eyebrow: "Qué nos diferencia",
      title: "Un proceso que ayuda a ver el evento antes de montarlo.",
      description:
        "Esto no es styling casual de fiesta. El trabajo empieza por definir el look, leer el espacio, ordenar lo que necesitan los suplidores y darle al cliente una imagen más clara antes del montaje.",
      items: [
        {
          title: "Secuencia clara de planificación",
          description:
            "El trabajo pasa por intake, moodboard, espacio, decoración digital, suplidores y manejo del día del evento.",
        },
        {
          title: "Dirección visual definida",
          description:
            "Cada concepto se trabaja con proporción, textura, paleta y el tono propio de la ocasión.",
        },
        {
          title: "Decoración física y digital",
          description:
            "La decoración se planifica en pantalla y en el espacio para que el cliente entienda el montaje antes de producir.",
        },
        {
          title: "Coordinación de suplidores",
          description:
            "Los suplidores reciben el concepto, los tiempos, las notas de producción y las confirmaciones finales que necesitan para trabajar desde el mismo plan.",
        },
        {
          title: "Manejo del día del evento",
          description:
            "El día del evento, el equipo observa el montaje, el timeline, el flujo de suplidores y los detalles visibles para invitados.",
        },
      ],
    },
    servicesList: {
      eyebrow: "Vista de servicios",
      title: "Elige el servicio que encaja con la etapa de tu evento.",
      description:
        "Cada servicio responde a una necesidad distinta: el look, la decoración, el plan completo, los suplidores o el manejo del día.",
      navigatorTitle:
        "Usa los detalles de abajo para ver qué cubre cada servicio y cuándo conviene.",
      serviceCountLabel: "servicios",
      decisionTitle: "¿Todavía no sabes por dónde comenzar?",
      decisionDescription:
        "Usa la etapa actual de tu evento para escoger el mejor punto de partida.",
      items: [
        {
          title: "Diseño de Eventos",
          summary:
            "Dirección creativa para eventos que necesitan definir el look antes de tomar decisiones grandes de planificación.",
          description:
            "Diseño de Eventos define mood, paleta, referencias de layout y dirección de styling antes de tomar decisiones de producción.",
          audience:
            "Ideal para clientes que saben que quieren un evento pulido, pero necesitan ayuda definiendo look, paleta y atmósfera.",
          highlights: [
            "Desarrollo de moodboard",
            "Dirección de paleta de colores",
            "Dirección de concepto",
            "Inspiración de tablescape",
            "Guía de layout y styling",
            "Notas de diseño para detalles clave",
          ],
          ctaHref: "/inquiry",
          ctaLabel: "Consultar sobre diseño de eventos",
        },
        {
          title: "Decoración",
          summary:
            "Planificación de decoración física y digital para clientes que necesitan que el espacio se vea terminado y listo para invitados.",
          description:
            "Decoración convierte la dirección de diseño en un montaje real mediante styling, colocación, previsualizaciones digitales y notas de montaje.",
          audience:
            "Ideal para clientes que tienen un mood o tema, pero necesitan ayuda para llevarlo al espacio real.",
          highlights: [
            "Planificación de decoración física",
            "Conceptos de decoración digital",
            "Dirección de montaje",
            "Guía de styling decorativo",
            "Coordinación entre áreas clave",
          ],
          ctaHref: "/inquiry",
          ctaLabel: "Consultar sobre decoración",
        },
        {
          title: "Planificación Completa",
          summary:
            "Planificación completa para clientes que quieren manejar juntos diseño, logística, suplidores y detalles del día del evento.",
          description:
            "Planificación Completa le da al evento una sola ruta de trabajo, desde la estructura inicial hasta los detalles finales del día.",
          audience:
            "Ideal para clientes que no quieren manejar diseño, suplidores, tiempos y logística en piezas separadas.",
          highlights: [
            "Estructura de planificación",
            "Guía de timeline",
            "Coordinación de suplidores",
            "Dirección de diseño",
            "Logística del evento",
            "Supervisión del día del evento",
          ],
          ctaHref: "/inquiry",
          ctaLabel: "Consultar sobre planificación completa",
        },
        {
          title: "Coordinación de Suplidores",
          summary:
            "Apoyo de comunicación para que los suplidores seleccionados entiendan el look, los tiempos, sus responsabilidades y las expectativas.",
          description:
            "Coordinación de Suplidores organiza cotizaciones, responsabilidades, tiempos y detalles finales antes de producción y montaje.",
          audience:
            "Ideal para clientes que ya comenzaron a escoger suplidores, pero necesitan más seguimiento y organización.",
          highlights: [
            "Apoyo en búsqueda de suplidores",
            "Coordinación de cotizaciones",
            "Guía de comunicación",
            "Notas para suplidores según el concepto",
            "Confirmaciones finales de suplidores",
          ],
          ctaHref: "/inquiry",
          ctaLabel: "Consultar sobre coordinación de suplidores",
        },
        {
          title: "Manejo del Día del Evento",
          summary:
            "Supervisión presencial para eventos ya planificados que necesitan manejo estable el mismo día.",
          description:
            "Manejo del Día del Evento cubre montaje, tiempos, comunicación con suplidores y logística clave mientras el evento sucede.",
          audience:
            "Ideal para clientes que ya tienen el plan y quieren a alguien con experiencia mirando las piezas en movimiento.",
          highlights: [
            "Timeline del día del evento",
            "Check-ins con suplidores",
            "Supervisión de montaje",
            "Apoyo al flujo de invitados",
            "Manejo logístico",
            "Supervisión final del día",
          ],
          ctaHref: "/inquiry",
          ctaLabel: "Consultar sobre manejo del evento",
        },
      ],
      decisionItems: [
        {
          title: "El look todavía no está definido",
          description:
            "Empieza con Diseño de Eventos cuando la pregunta principal es cómo debe verse y sentirse la celebración.",
          service: "Diseño de Eventos",
        },
        {
          title: "El mood está claro, pero el espacio no",
          description:
            "Empieza con Decoración cuando la idea existe, pero el montaje necesita colocación, styling y dirección práctica.",
          service: "Decoración",
        },
        {
          title: "Quieres una sola aliada de planificación",
          description:
            "Empieza con Planificación Completa cuando diseño, logística, suplidores y detalles del día deben manejarse juntos.",
          service: "Planificación Completa",
        },
        {
          title: "Ya hay suplidores, pero el plan se siente suelto",
          description:
            "Empieza con Coordinación de Suplidores cuando la comunicación, cotizaciones, responsabilidades y confirmaciones necesitan un sistema más claro.",
          service: "Coordinación de Suplidores",
        },
        {
          title: "El plan existe, pero el día del evento necesita una líder",
          description:
            "Empieza con Manejo del Día del Evento cuando el evento ya está planificado y el día necesita a alguien manejando los detalles.",
          service: "Manejo del Día del Evento",
        },
      ],
    },
    servicesSupportCta: {
      eyebrow: "¿Necesitas ayuda escogiendo?",
      title: "¿No sabes dónde encaja tu evento?",
      description:
        "Envía los detalles que tengas. JPL Atelier te ayudará a escoger el servicio que más sentido haga.",
      href: "/inquiry",
      label: "Comenzar consulta",
    },
    processJourney: {
      eyebrow: "La experiencia de planificación",
      title: "Cómo se desarrolla el proceso",
      description:
        "El flujo de seis pasos mantiene organizados el look, el espacio, los suplidores y el día del evento.",
      steps: [
        {
          title: "Paso 1: Formulario",
          description:
            "El formulario recoge el tipo de evento, prioridades, necesidades y expectativas.",
        },
        {
          title: "Paso 2: Moodboard",
          description:
            "El moodboard define paleta, texturas, styling y atmósfera.",
        },
        {
          title: "Paso 3: Espacio",
          description:
            "Se revisa el espacio para entender flujo, layout, puntos focales y oportunidades de diseño.",
        },
        {
          title: "Paso 4: Decoración Digital",
          description:
            "Las previsualizaciones digitales muestran la dirección decorativa antes de producir.",
        },
        {
          title: "Paso 5: Suplidores",
          description:
            "La coordinación de suplidores organiza cotizaciones, responsabilidades, tiempos, comunicación y confirmaciones finales.",
        },
        {
          title: "Paso 6: Día del Evento",
          description:
            "El manejo presencial observa montaje, flujo de suplidores, timeline y detalles finales del día.",
        },
      ],
    },
    lookbookGallery: {
      eyebrow: "Lenguaje visual",
      title:
        "Una mirada al mood, la sobriedad y el detalle detrás de JPL Atelier.",
      description:
        "Esta página muestra el gusto y las referencias visuales del estudio mientras el portafolio de eventos completados sigue creciendo.",
      items: [
        {
          alt: "Tablescape neutral con styling floral editorial",
          caption:
            "Dirección de mesa basada en proporción, textura y sobriedad.",
          src: "https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=1200&q=80",
        },
        {
          alt: "Ceremonia refinada con composición floral moderna",
          caption:
            "Referencias de ceremonia para balance, espacio y composición floral sobria.",
          src: "https://images.unsplash.com/photo-1519225421980-715cb0215aed?auto=format&fit=crop&w=1200&q=80",
        },
        {
          alt: "Detalles de recepción de lujo con luz cálida de velas",
          caption:
            "Referencias de styling para recepciones y celebraciones formales de noche.",
          src: "https://images.unsplash.com/photo-1469371670807-013ccf25f16a?auto=format&fit=crop&w=1200&q=80",
        },
        {
          alt: "Arreglo floral elegante sobre mesa blanca",
          caption:
            "Estudios de paleta y flores con neutros cálidos y detalle cuidado.",
          src: "https://images.unsplash.com/photo-1511285560929-80b456fea0bc?auto=format&fit=crop&w=1200&q=80",
        },
        {
          alt: "Montaje sofisticado con texturas neutrales",
          caption:
            "Referencias de detalle para tono, materiales y experiencia en mesa.",
          src: "https://images.unsplash.com/photo-1519167758481-83f550bb49b3?auto=format&fit=crop&w=1200&q=80",
        },
        {
          alt: "Detalle de venue con luz editorial y styling refinado",
          caption:
            "Referencias de espacio para eventos corporativos, reuniones de lujo y celebraciones privadas.",
          src: "https://images.unsplash.com/photo-1513278974582-3e1b4a4fa21d?auto=format&fit=crop&w=1200&q=80",
        },
      ],
    },
  },
  inquiry: {
    eyebrow: "Comienza tu evento",
    title: "Comparte los detalles para guiarte al servicio adecuado.",
    description:
      "Cada evento trae necesidades distintas. El servicio correcto depende del tipo de evento, lugar, etapa de planificación, diseño, suplidores, flujo de invitados y logística del día.",
    asideTitle: "Antes de enviar tu consulta",
    availability: [
      "JPL Atelier se enfoca en bodas, eventos corporativos, eventos de lujo y celebraciones senior 18+.",
      "Comparte lo que ya sepas: fecha, lugar, cantidad de invitados, tipo de evento, suplidores y dónde necesitas ayuda.",
    ],
    formEyebrow: "Formulario de consulta",
    formIntro:
      "Llena el formulario con los detalles del evento y tus prioridades de planificación.",
    privacyNote:
      "Tu consulta se guarda para que el equipo revise los detalles y dé seguimiento sobre fit, alcance y próximos pasos.",
    submitError:
      "La consulta no pudo enviarse ahora mismo. Inténtalo de nuevo.",
    submitPending: "Enviando consulta...",
    submitLabel: "Enviar consulta",
    submitSuccess:
      "Tu consulta fue enviada. El equipo puede revisar los detalles del evento y darte seguimiento.",
    successHint:
      "Luego, el estudio revisa el tipo de evento, las necesidades de planificación y el nivel de ayuda solicitado.",
    actions: [
      { href: "/services", label: "Revisar servicios" },
      { href: "/process", label: "Ver el proceso" },
    ],
    fields: [
      {
        autocomplete: "name",
        label: "Nombre",
        name: "name",
        placeholder: "Tu nombre completo",
        required: true,
        type: "text",
      },
      {
        autocomplete: "email",
        label: "Correo",
        name: "email",
        placeholder: "nombre@ejemplo.com",
        required: true,
        type: "email",
      },
      {
        autocomplete: "tel",
        label: "Teléfono",
        name: "phone",
        placeholder: "(787) 000-0000",
        required: true,
        type: "tel",
      },
      {
        label: "Tipo de evento",
        name: "eventType",
        options: [
          { label: "Selecciona el tipo de evento", value: "" },
          { label: "Boda", value: "Boda" },
          { label: "Evento corporativo", value: "Evento corporativo" },
          { label: "Evento de lujo", value: "Evento de lujo" },
          { label: "Celebración senior 18+", value: "Celebración senior 18+" },
        ],
        required: true,
        type: "select",
      },
    ],
  },
  legal: {
    description:
      "Esta página explica cómo JPL Atelier usa la información de consulta y qué cubre el contenido del sitio.",
    sections: [
      {
        title: "Aviso de privacidad",
        body: [
          "La información enviada por el formulario se usa para revisar solicitudes de eventos, responder mensajes y continuar conversaciones de planificación relacionadas con los servicios de JPL Atelier.",
          "Los datos solo se comparten con proveedores necesarios para operar el sitio web, gestionar comunicaciones o apoyar el proceso de consulta.",
          "Para actualizar o eliminar información previamente compartida a través del sitio, comunícate directamente con el negocio usando el método de contacto provisto por el estudio.",
        ],
      },
      {
        title: "Términos",
        body: [
          "El contenido del sitio es información general sobre servicios, proceso, enfoque de eventos y dirección de marca. Enviar una consulta no crea una reserva, un contrato ni una garantía de disponibilidad.",
          "El alcance del proyecto, precios, tiempos y entregables se confirman luego mediante comunicación directa y cualquier acuerdo final aprobado por ambas partes.",
          "JPL Atelier puede actualizar el contenido del sitio, las descripciones de servicios o esta página cuando cambien los detalles del negocio o sus prácticas operativas.",
        ],
      },
    ],
    title: "Privacidad y términos",
    updatedAt: "2026-05-08",
  },
};
